import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import type { EventStatus } from '@cookspace-shared-contracts/core';

import { useListEventsQuery } from '@/api/eventsApi';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';

const STATUS_OPTIONS: Array<{ value: EventStatus; label: string }> = [
    { value: 'scheduled', label: 'Запланировано' },
    { value: 'ongoing', label: 'Идёт' },
    { value: 'completed', label: 'Завершено' },
    { value: 'cancelled', label: 'Отменено' }
];

const PREMIUM_OPTIONS: Array<{ value: 'true' | 'false'; label: string }> = [
    { value: 'true', label: 'Премиум' },
    { value: 'false', label: 'Обычные' }
];

const STATUS_LABEL: Record<EventStatus, string> = {
    scheduled: 'Запланировано',
    ongoing: 'Идёт',
    completed: 'Завершено',
    cancelled: 'Отменено'
};

const STATUS_COLOR: Record<EventStatus, string> = {
    scheduled: 'bg-blue-100 text-blue-800',
    ongoing: 'bg-red-100 text-red-800',
    completed: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-yellow-100 text-yellow-800'
};

function formatDateTime(iso: string): string {
    return new Date(iso).toLocaleString('ru-RU');
}

function formatRating(value: number | null): string {
    return value === null ? '—' : value.toFixed(1);
}

export function EventsPage(): ReactElement {
    const [searchInput, setSearchInput] = useState('');
    const [submittedSearch, setSubmittedSearch] = useState('');
    const [filterStatus, setFilterStatus] = useState<EventStatus | ''>('');
    const [filterPremium, setFilterPremium] = useState<'' | 'true' | 'false'>('');

    const queryArg = useMemo(
        () => ({
            ...(submittedSearch ? { search: submittedSearch } : {}),
            ...(filterStatus ? { status: filterStatus } : {}),
            ...(filterPremium === '' ? {} : { isPremium: filterPremium === 'true' }),
            limit: 50,
            offset: 0
        }),
        [submittedSearch, filterStatus, filterPremium]
    );

    const { data, isLoading, isError } = useListEventsQuery(queryArg);

    const items = data?.items ?? [];

    return (
        <div data-testid="events-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Мероприятия</h1>
                {data && (
                    <span className="text-sm text-gray-500" data-testid="events-total">
                        Всего: {data.total}
                    </span>
                )}
            </div>

            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    setSubmittedSearch(searchInput.trim());
                }}
                className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-white p-3"
                data-testid="events-filters"
            >
                <div className="min-w-[240px] flex-1">
                    <Input
                        label="Поиск"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Название мероприятия"
                        data-testid="events-search-input"
                    />
                </div>
                <div className="w-48">
                    <Select
                        label="Статус"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value as EventStatus | '')}
                        placeholder="Все статусы"
                        options={STATUS_OPTIONS}
                        data-testid="events-filter-status"
                    />
                </div>
                <div className="w-48">
                    <Select
                        label="Премиум"
                        value={filterPremium}
                        onChange={(e) =>
                            setFilterPremium(e.target.value as '' | 'true' | 'false')
                        }
                        placeholder="Все"
                        options={PREMIUM_OPTIONS}
                        data-testid="events-filter-premium"
                    />
                </div>
                <button
                    type="submit"
                    data-testid="events-search-submit"
                    className="rounded bg-gray-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-gray-800"
                >
                    Найти
                </button>
            </form>

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="events-error">
                    Не удалось загрузить мероприятия
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="events-table"
                >
                    <div className="grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_160px_120px_120px_80px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>ID</span>
                        <span>Название</span>
                        <span>Автор</span>
                        <span>Адрес</span>
                        <span>Запланировано</span>
                        <span>Статус</span>
                        <span className="text-right">Участники</span>
                        <span className="text-right">Рейтинг</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Мероприятия не найдены
                        </div>
                    )}

                    {items.map((event) => (
                        <div
                            key={event.id}
                            className="grid grid-cols-[80px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)_160px_120px_120px_80px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`event-row-${event.id}`}
                        >
                            <span className="truncate font-mono text-xs text-gray-500">
                                {event.id.slice(0, 8)}
                            </span>
                            <span className="truncate text-gray-900">{event.title}</span>
                            <span className="truncate text-gray-700">
                                {event.authorDisplayName}
                            </span>
                            <span className="truncate text-gray-700">{event.address}</span>
                            <span className="text-xs text-gray-600">
                                {formatDateTime(event.scheduledAt)}
                            </span>
                            <span>
                                <span
                                    className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_COLOR[event.status]}`}
                                    data-testid={`event-status-${event.id}`}
                                >
                                    {STATUS_LABEL[event.status]}
                                </span>
                            </span>
                            <span className="text-right text-gray-700">
                                {event.currentParticipants}/{event.maxParticipants}
                            </span>
                            <span className="text-right text-gray-700">
                                {formatRating(event.averageRating)}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
